import React from "react";
import { Head, router, usePage, useForm } from "@inertiajs/react";
import styled from "styled-components";
import { getCsrfToken } from "../../util";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import PortalApp from "../../portal/App";
import { openXendit, payViaPopup } from "../../portal/payments";
import {
  PortalContainer,
  PortalSection,
  PortalCard,
  PortalEmpty,
} from "../../portal/PortalCard";
import {
  brand_orange_deep,
  brand_gradient,
  narrowScreenMediaQuery,
} from "../../constants/styles";

const TABS = [
  { key: "brevet", label: "Sertifikat Brevet & Ujian" },
  { key: "webinar", label: "Sertifikat Webinar" },
  { key: "video", label: "Sertifikat Video" },
];

function initialTab() {
  if (typeof window === "undefined") return "brevet";
  const hash = window.location.hash.replace("#", "");
  return TABS.some((t) => t.key === hash) ? hash : "brevet";
}

export default function Certificates() {
  const { certificates = {}, endpoints = {} } = usePage().props;
  const [tab, setTab] = React.useState(initialTab);

  return (
    <>
      <Head>
        <title>Sertifikat — Taxsam Learning Center</title>
        <meta
          name="description"
          content="Klaim dan unduh sertifikat Brevet, Ujian, Webinar, dan Video Anda."
        />
      </Head>
      <PortalContainer>
        <PortalSection
          title="Sertifikat Digital"
          subtitle="Kelola sertifikat dari modul Brevet, ujian sertifikasi, webinar, dan video pembelajaran."
        >
          <Segmented role="tablist" aria-label="Jenis sertifikat">
            {TABS.map((t) => (
              <SegmentBtn
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                $active={tab === t.key}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </SegmentBtn>
            ))}
          </Segmented>

          <SectionErrorBoundary name={`CertTab:${tab}`}>
            {tab === "brevet" && (
              <BrevetList
                items={certificates.brevet || []}
                endpoints={endpoints}
              />
            )}
            {tab === "webinar" && (
              <WebinarList
                items={certificates.webinar || []}
                endpoints={endpoints}
              />
            )}
            {tab === "video" && (
              <VideoList
                items={certificates.video || []}
                endpoints={endpoints}
              />
            )}
          </SectionErrorBoundary>
        </PortalSection>
      </PortalContainer>
    </>
  );
}

Certificates.layout = (page) => <PortalApp>{page}</PortalApp>;

// ---- Brevet (course-grouped, with curriculums under) -----------------------

function BrevetList({ items, endpoints }) {
  if (!items || items.length === 0) {
    return (
      <PortalEmpty>
        Belum ada sertifikat Brevet yang tersedia. Mulai mengikuti modul untuk
        membuka sertifikat.
      </PortalEmpty>
    );
  }
  return (
    <Stack>
      {items.map((g) => (
        <BrevetCard key={g.id} group={g} endpoints={endpoints} />
      ))}
    </Stack>
  );
}

function BrevetCard({ group, endpoints }) {
  const [open, setOpen] = React.useState(false);
  const statusLabel = STATUS_LABELS[group.status] || STATUS_LABELS.in_progress;
  return (
    <Card>
      <CardHead>
        <CardTitleBlock>
          <CardEyebrow $tone={statusLabel.tone}>
            {statusLabel.label}
          </CardEyebrow>
          <CardTitle>{group.name}</CardTitle>
        </CardTitleBlock>
        <CardActions>
          {group.download && <DownloadButton href={group.download.url} />}
          {group.claim && (
            <ClaimButton claim={group.claim} endpoints={endpoints} />
          )}
          {group.pay && (
            <CourseCertPayButton
              pay={group.pay}
              title={group.name}
              endpoints={endpoints}
            />
          )}
          <ToggleButton
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? "Tutup" : "Lihat Detail"}
          </ToggleButton>
        </CardActions>
      </CardHead>
      {open && group.curriculums && group.curriculums.length > 0 && (
        <CurriculumList>
          {group.curriculums.map((cur) => (
            <CurriculumRow key={cur.id}>
              <CurriculumName>{cur.name}</CurriculumName>
              <CurriculumStatus $status={cur.status}>
                {EXAM_STATUS_LABEL[cur.status] || "Belum Ujian"}
                {cur.score != null && ` · ${cur.score}`}
              </CurriculumStatus>
              <CurriculumActions>
                {cur.download && (
                  <DownloadButton href={cur.download.url} compact />
                )}
                {cur.claim && (
                  <ClaimButton
                    claim={cur.claim}
                    endpoints={endpoints}
                    compact
                  />
                )}
              </CurriculumActions>
            </CurriculumRow>
          ))}
        </CurriculumList>
      )}
    </Card>
  );
}

// ---- Webinar (flat row) ----------------------------------------------------

function WebinarList({ items, endpoints }) {
  if (!items || items.length === 0) {
    return (
      <PortalEmpty>Belum ada sertifikat webinar yang tersedia.</PortalEmpty>
    );
  }
  return (
    <Stack>
      {items.map((row) => (
        <RowCard key={row.id}>
          <RowIcon aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <rect
                x="3"
                y="6"
                width="18"
                height="14"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M3 10h18M8 3v6M16 3v6"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </RowIcon>
          <RowBody>
            {(() => {
              const statusLabel =
                STATUS_LABELS[row.status] || STATUS_LABELS.in_progress;
              return (
                <CardEyebrow $tone={statusLabel.tone}>
                  {statusLabel.label}
                </CardEyebrow>
              );
            })()}
            {row.detail_url ? (
              <RowTitleLink href={row.detail_url}>{row.title}</RowTitleLink>
            ) : (
              <RowTitle>{row.title}</RowTitle>
            )}
            <RowMeta>
              {row.speaker && <span>{row.speaker}</span>}
              {row.speaker && row.date_iso && <span aria-hidden="true">·</span>}
              {row.date_iso && <span>{formatDateID(row.date_iso)}</span>}
              {row.detail_url && (
                <RowDetailLink href={row.detail_url}>
                  Lihat webinar →
                </RowDetailLink>
              )}
            </RowMeta>
            {row.pending && (
              <PendingNote $expired={row.pending.state === "expired"}>
                {row.pending.state === "expired"
                  ? `Pembayaran kedaluwarsa${row.pending.expired_at ? ` (${formatDateID(row.pending.expired_at)})` : ""}. Silakan buat ulang.`
                  : `Menunggu pembayaran${row.pending.price_formatted ? ` ${row.pending.price_formatted}` : ""}${row.pending.expired_at ? ` · berlaku sampai ${formatDateID(row.pending.expired_at)}` : ""}.`}
              </PendingNote>
            )}
          </RowBody>
          <RowActions>
            {row.download && <DownloadButton href={row.download.url} />}
            {row.claim && (
              <ClaimButton claim={row.claim} endpoints={endpoints} />
            )}
            {row.pending && <WebinarPendingButton pending={row.pending} />}
            {!row.claim && !row.download && !row.pending && row.pay && (
              <PayButton pay={row.pay} title={row.title} />
            )}
          </RowActions>
        </RowCard>
      ))}
    </Stack>
  );
}

// ---- Video (flat row) ------------------------------------------------------

function VideoList({ items }) {
  if (!items || items.length === 0) {
    return (
      <PortalEmpty>
        Sertifikat video akan muncul di sini saat tersedia.
      </PortalEmpty>
    );
  }
  return (
    <Stack>
      {items.map((row) => (
        <RowCard key={row.id}>
          <RowIcon aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path d="M10 9l5 3-5 3v-6z" fill="currentColor" />
            </svg>
          </RowIcon>
          <RowBody>
            <RowTitle>{row.title}</RowTitle>
            <RowMeta>
              {row.curriculum && <span>{row.curriculum}</span>}
              {row.curriculum && row.date_iso && (
                <span aria-hidden="true">·</span>
              )}
              {row.date_iso && <span>{formatDateID(row.date_iso)}</span>}
            </RowMeta>
          </RowBody>
          <RowActions>
            {row.download && <DownloadButton href={row.download.url} />}
          </RowActions>
        </RowCard>
      ))}
    </Stack>
  );
}

// ---- Buttons ---------------------------------------------------------------

function ClaimButton({ claim, endpoints, compact = false }) {
  // UserCertificatesController#create_certificate + CreateService read these
  // flat (params[:base_resource_type], .permit(:resource_id, ...)) — NOT
  // nested under a `user_certificate` key. Wrapping them dropped every field
  // (Unpermitted parameter), so the claim 422'd with "Base resource harus ada".
  const form = useForm({
    resource_type: claim.resource_type,
    resource_id: claim.resource_id,
    base_resource_type: claim.base_resource_type,
    base_resource_id: claim.base_resource_id,
  });
  const submit = (e) => {
    e.preventDefault();
    if (!endpoints.create_url) return;
    form.post(endpoints.create_url, { preserveScroll: true });
  };
  return (
    <ClaimForm onSubmit={submit}>
      <PrimaryBtn type="submit" disabled={form.processing} $compact={compact}>
        {form.processing ? "Memproses…" : claim.label || "Klaim"}
      </PrimaryBtn>
    </ClaimForm>
  );
}

function PayButton({ pay, title, compact = false }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <PrimaryBtn
        type="button"
        onClick={() => setOpen(true)}
        $compact={compact}
      >
        {pay.label || "Bayar Sertifikat"}
      </PrimaryBtn>
      {open && (
        <WebinarPayModal
          pay={pay}
          title={title}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// An initiated-but-unpaid webinar invoice. Active → reopen the existing Xendit
// link in a window. Expired (our clock) → reinitiate: the backend voids the
// stale invoice and mints a fresh one, opened via popup.
function WebinarPendingButton({ pending }) {
  const [busy, setBusy] = React.useState(false);

  if (pending.state === "active") {
    return (
      <PrimaryBtn type="button" onClick={() => openXendit(pending.xendit_url)}>
        {pending.label || "Lanjut Bayar"}
      </PrimaryBtn>
    );
  }

  const reinitiate = async () => {
    setBusy(true);
    const res = await payViaPopup(pending.reinitiate_url, {});
    setBusy(false);
    if (res.ok) router.reload({ only: ["certificates", "profile"] });
  };

  return (
    <PrimaryBtn type="button" disabled={busy} onClick={reinitiate}>
      {busy ? "Memproses..." : pending.label || "Buat Ulang Pembayaran"}
    </PrimaryBtn>
  );
}

// Rebrand replacement for the legacy Metronic "Buka Kunci Sertifikat" modal.
// Pays via a popup window (payViaPopup) so the Xendit invoice opens in its own
// window and the portal tab is preserved — consistent with every other Xendit
// hand-off. The backend reuses an existing unpaid invoice if one is still live.
function WebinarPayModal({ pay, title, onClose }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const pay_now = async () => {
    setError("");
    setBusy(true);
    const res = await payViaPopup(pay.create_url, {});
    setBusy(false);
    if (res.ok) {
      onClose();
      router.reload({ only: ["certificates", "profile"] });
    } else {
      setError(res.error || "Gagal memproses pembayaran.");
    }
  };

  return (
    <PayBackdrop
      role="dialog"
      aria-modal="true"
      aria-labelledby="webinar-pay-title"
      onClick={onClose}
    >
      <PayCard onClick={(e) => e.stopPropagation()}>
        <PayTitle id="webinar-pay-title">Bayar Sertifikat Webinar</PayTitle>
        <PayLede>
          Terbitkan sertifikat kemampuan untuk webinar yang telah Anda ikuti.
        </PayLede>
        <PaySummary>
          <PaySummaryName>{title}</PaySummaryName>
          <PaySummaryRow>
            <span>Total Pembayaran</span>
            <strong>Rp {formatIdr(pay.price_idr)}</strong>
          </PaySummaryRow>
        </PaySummary>
        {error && <HintErr>{error}</HintErr>}
        <PaySubmit type="button" disabled={busy} onClick={pay_now}>
          {busy ? "Memproses..." : "Bayar Sekarang"}
        </PaySubmit>
        {pay.subscribe_url && (
          <PaySubscribe href={pay.subscribe_url}>
            Sering ikut webinar? Berlangganan paket untuk klaim sertifikat tanpa
            biaya tambahan →
          </PaySubscribe>
        )}
        <PayCancel type="button" onClick={onClose}>
          Batal
        </PayCancel>
      </PayCard>
    </PayBackdrop>
  );
}

function CourseCertPayButton({ pay, title, endpoints }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <PrimaryBtn type="button" onClick={() => setOpen(true)}>
        {pay.label || "Bayar Sertifikat"}
      </PrimaryBtn>
      {open && (
        <CourseCertPayModal
          pay={pay}
          title={title}
          endpoints={endpoints}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// Rebrand of the legacy Metronic course-certificate modal. Pays for a brevet
// certificate via the existing UserCourseCertificate flow: an optional
// subscription bundle slot (free), an optional live coupon, auto-applied
// balance, and the Xendit remainder. Submits as a NATIVE form POST so the
// controller's `redirect_to payment.xendit_url` is a real browser navigation.
function CourseCertPayModal({ pay, title, endpoints, onClose }) {
  const { profile = {} } = usePage().props;
  const [bundleId, setBundleId] = React.useState("");
  const [couponInput, setCouponInput] = React.useState("");
  const [appliedCode, setAppliedCode] = React.useState("");
  const [coupon, setCoupon] = React.useState({ status: "idle" });
  const [referralInput, setReferralInput] = React.useState("");
  const [referral, setReferral] = React.useState({ status: "idle" });
  const [quote, setQuote] = React.useState({
    total_formatted: pay.price_formatted,
    free: false,
  });
  const [quoting, setQuoting] = React.useState(false);

  const jsonHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-CSRF-Token": getCsrfToken(),
  });

  const refreshQuote = async (code, claimId) => {
    setQuoting(true);
    try {
      const res = await fetch(endpoints.course_certificate_quote_url, {
        method: "POST",
        headers: jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          course_id: pay.course_id,
          code: code || "",
          certificate_claim_id: claimId || "",
          balance: "true",
        }),
      });
      const data = await res.json();
      // Only adopt a response that carries totals. An invalid-coupon reply
      // ({ coupon_valid: false, message }) has none — keep the prior total.
      if (res.ok && data.total_formatted) setQuote(data);
      return data;
    } catch (_) {
      return null;
    } finally {
      setQuoting(false);
    }
  };

  React.useEffect(() => {
    refreshQuote("", "");
  }, []);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCoupon({ status: "checking" });
    const data = await refreshQuote(code, bundleId);
    if (data && data.coupon_valid) {
      setAppliedCode(code);
      setCoupon({ status: "valid", discount: data.discount_formatted });
    } else {
      setCoupon({
        status: "invalid",
        message: (data && data.message) || "Kupon tidak ditemukan.",
      });
    }
  };

  const clearCoupon = () => {
    setCouponInput("");
    setAppliedCode("");
    setCoupon({ status: "idle" });
    refreshQuote("", bundleId);
  };

  const selectBundle = (id) => {
    setBundleId(id);
    refreshQuote(appliedCode, id);
  };

  const validateReferral = async () => {
    const code = referralInput.trim();
    if (!code) {
      setReferral({ status: "idle" });
      return;
    }
    if (code.length !== 6) {
      setReferral({
        status: "invalid",
        message: "Format kode referral harus 6 karakter.",
      });
      return;
    }
    setReferral({ status: "checking" });
    try {
      const res = await fetch(
        `${endpoints.validate_referral_url}?referral_code=${encodeURIComponent(code)}`,
        { method: "POST", headers: jsonHeaders(), credentials: "same-origin" },
      );
      setReferral(
        res.ok
          ? { status: "valid" }
          : { status: "invalid", message: "Kode referral tidak valid." },
      );
    } catch (_) {
      setReferral({
        status: "invalid",
        message: "Gagal memvalidasi kode referral.",
      });
    }
  };

  const couponApplied = coupon.status === "valid";
  const referralPending =
    referralInput.trim().length > 0 && referral.status !== "valid";
  const [fullName, setFullName] = React.useState(profile.name || "");
  const [phone, setPhone] = React.useState(profile.phone || "");
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState("");
  const submitDisabled = quoting || referralPending || paying;

  const submitCert = async () => {
    setPayError("");
    setPaying(true);
    const res = await payViaPopup(endpoints.course_certificate_create_url, {
      course_id: pay.course_id,
      balance: "true",
      certificate_claim_id: bundleId || "",
      coupon_id: !bundleId && couponApplied ? quote.coupon_id : "",
      referral_code: referralInput.trim(),
      full_name: fullName,
      phone_number: phone,
    });
    setPaying(false);
    if (res.paid) {
      onClose();
      router.reload({ only: ["certificates", "profile"] });
      return;
    }
    if (res.ok) {
      onClose();
      router.reload({ only: ["certificates", "profile"] });
      return;
    }
    setPayError(res.error || "Gagal memproses pembayaran.");
  };

  return (
    <PayBackdrop
      role="dialog"
      aria-modal="true"
      aria-labelledby="course-pay-title"
      onClick={onClose}
    >
      <PayCard onClick={(e) => e.stopPropagation()}>
        <PayTitle id="course-pay-title">Klaim Sertifikat Brevet</PayTitle>
        <PayLede>
          Pilih cara klaim sertifikat untuk <strong>{title}</strong>.
        </PayLede>

        {pay.bundle_slots && pay.bundle_slots.length > 0 && (
          <OptionGroup>
            <OptionLabel>Gunakan paket bundling (gratis)</OptionLabel>
            <OptionRow
              type="button"
              $active={bundleId === ""}
              onClick={() => selectBundle("")}
            >
              Bayar dengan kupon / saldo / uang
            </OptionRow>
            {pay.bundle_slots.map((slot) => (
              <OptionRow
                key={slot.id}
                type="button"
                $active={bundleId === slot.id}
                onClick={() => selectBundle(slot.id)}
              >
                Pakai {slot.name} (gratis)
              </OptionRow>
            ))}
          </OptionGroup>
        )}

        <PaySummary>
          <PaySummaryRow>
            <span>Harga Sertifikat</span>
            <span>{pay.price_formatted}</span>
          </PaySummaryRow>
          {couponApplied && coupon.discount && (
            <PaySummaryRow>
              <span>Potongan Kupon</span>
              <span>- {coupon.discount}</span>
            </PaySummaryRow>
          )}
          <PaySummaryRow>
            <span>Saldo Tersedia</span>
            <span>{quote.balance_formatted || pay.balance_formatted}</span>
          </PaySummaryRow>
          <PaySummaryRow>
            <strong>Total Pembayaran</strong>
            <strong>{quoting ? "..." : quote.total_formatted}</strong>
          </PaySummaryRow>
        </PaySummary>

        {bundleId === "" && (
          <CertField>
            <span>Kode Kupon (opsional)</span>
            <CouponRow>
              <input
                value={couponInput}
                disabled={couponApplied || coupon.status === "checking"}
                onChange={(e) => setCouponInput(e.target.value)}
                placeholder="Masukkan kode kupon"
                style={{ textTransform: "uppercase" }}
              />
              {couponApplied ? (
                <ApplyBtn type="button" onClick={clearCoupon}>
                  Hapus
                </ApplyBtn>
              ) : (
                <ApplyBtn
                  type="button"
                  onClick={applyCoupon}
                  disabled={coupon.status === "checking" || !couponInput.trim()}
                >
                  {coupon.status === "checking" ? "Memeriksa..." : "Terapkan"}
                </ApplyBtn>
              )}
            </CouponRow>
            {coupon.status === "invalid" && <HintErr>{coupon.message}</HintErr>}
          </CertField>
        )}

        <div>
          {!profile.complete && (
            <CertFieldset>
              <CertFieldLabel>
                Lengkapi data berikut untuk melanjutkan pembayaran.
              </CertFieldLabel>
              <CertField>
                <span>Nama Lengkap</span>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Nama lengkap"
                />
              </CertField>
              <CertField>
                <span>Nomor Handphone</span>
                <input
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="08xxxxxxxxxx"
                />
              </CertField>
            </CertFieldset>
          )}

          <CertField>
            <span>Kode Referral (opsional)</span>
            <input
              value={referralInput}
              maxLength={6}
              placeholder="6 karakter"
              style={{ textTransform: "uppercase" }}
              onChange={(e) => {
                setReferralInput(e.target.value);
                setReferral({ status: "idle" });
              }}
              onBlur={validateReferral}
            />
          </CertField>
          {referral.status === "checking" && (
            <HintMuted>Memeriksa kode...</HintMuted>
          )}
          {referral.status === "valid" && <HintOk>Kode referral valid.</HintOk>}
          {referral.status === "invalid" && (
            <HintErr>{referral.message}</HintErr>
          )}

          {payError && <HintErr>{payError}</HintErr>}

          <PaySubmit
            type="button"
            disabled={submitDisabled}
            onClick={submitCert}
          >
            {paying
              ? "Memproses..."
              : quote.free
                ? "Klaim Sertifikat"
                : "Bayar Sekarang"}
          </PaySubmit>
        </div>

        <PayCancel type="button" onClick={onClose}>
          Batal
        </PayCancel>
      </PayCard>
    </PayBackdrop>
  );
}

function DownloadButton({ href, compact = false }) {
  return (
    <SecondaryBtn href={href} target="_blank" rel="noopener" $compact={compact}>
      <span>Unduh</span>
      <DownloadIcon viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 4v12m0 0l-5-5m5 5l5-5M5 20h14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </DownloadIcon>
    </SecondaryBtn>
  );
}

// ---- Helpers ---------------------------------------------------------------

const STATUS_LABELS = {
  claimed: { label: "Sertifikat Diterbitkan", tone: "success" },
  pending_review: { label: "Menunggu Review", tone: "muted" },
  eligible: { label: "Siap Diklaim", tone: "ok" },
  locked: { label: "Perlu Pembayaran", tone: "warn" },
  pending_payment: { label: "Menunggu Pembayaran", tone: "warn" },
  expired_payment: { label: "Pembayaran Kedaluwarsa", tone: "warn" },
  in_progress: { label: "Sedang Berjalan", tone: "muted" },
};

const EXAM_STATUS_LABEL = {
  passed: "Lulus Ujian",
  failed: "Belum Lulus",
  pending: "Menunggu",
  not_taken: "Belum Ujian",
};

function formatDateID(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (_) {
    return iso;
  }
}

function formatIdr(n) {
  return Number(n || 0).toLocaleString("id-ID");
}

// ---- Styles ----------------------------------------------------------------

const Segmented = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
  flex-wrap: wrap;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const SegmentBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  background: ${({ $active }) => ($active ? brand_gradient : "transparent")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#4b5563")};
  transition: filter 150ms ease;

  &:hover:not([aria-selected="true"]) {
    color: ${brand_orange_deep};
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    color: ${({ $active }) => ($active ? "#ffffff" : "#cbd5e1")};
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 8px;
`;

const Card = styled(PortalCard)`
  padding: 0;
`;

const CardHead = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 18px 22px;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const CardTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const CardEyebrow = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $tone }) =>
    $tone === "success"
      ? "#047857"
      : $tone === "ok"
        ? brand_orange_deep
        : $tone === "warn"
          ? "#b45309"
          : "#6b7280"};
  background: ${({ $tone }) =>
    $tone === "success"
      ? "rgba(16, 185, 129, 0.16)"
      : $tone === "ok"
        ? "rgba(216, 89, 7, 0.10)"
        : $tone === "warn"
          ? "rgba(245, 158, 11, 0.16)"
          : "rgba(15, 23, 42, 0.06)"};
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: #1f2937;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const CardActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ToggleButton = styled.button`
  background: transparent;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  padding: 9px 16px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;

  &:hover {
    border-color: ${brand_orange_deep};
    color: ${brand_orange_deep};
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.12);
    color: #cbd5e1;
    &:hover {
      color: #fdba74;
      border-color: #fdba74;
    }
  }
`;

const CurriculumList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0 22px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px dashed rgba(15, 23, 42, 0.08);

  [data-theme="dark"] & {
    border-top-color: rgba(255, 255, 255, 0.08);
  }
`;

const CurriculumRow = styled.li`
  display: grid;
  grid-template-columns: 1.4fr 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.02);

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const CurriculumName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const CurriculumStatus = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ $status }) =>
    $status === "passed"
      ? "#047857"
      : $status === "failed"
        ? "#b91c1c"
        : "#6b7280"};
`;

const CurriculumActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

const RowCard = styled(PortalCard)`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 16px 20px;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const RowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(216, 89, 7, 0.1);
  color: ${brand_orange_deep};

  [data-theme="dark"] & {
    background: rgba(216, 89, 7, 0.18);
    color: #fdba74;
  }
`;

const RowBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const RowTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const RowMeta = styled.span`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const RowTitleLink = styled.a`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &:hover {
    color: ${brand_orange_deep};
  }
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const RowDetailLink = styled.a`
  color: ${brand_orange_deep};
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

const ClaimForm = styled.form`
  display: inline-flex;
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: ${({ $compact }) => ($compact ? "8px 16px" : "10px 22px")};
  border-radius: 999px;
  border: none;
  background: ${brand_gradient};
  color: #ffffff;
  font-weight: 800;
  font-size: ${({ $compact }) => ($compact ? "11px" : "12px")};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover:not(:disabled) {
    filter: brightness(0.97);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
`;

const SecondaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ $compact }) => ($compact ? "7px 14px" : "9px 18px")};
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #ffffff;
  text-decoration: none;
  font-weight: 800;
  font-size: ${({ $compact }) => ($compact ? "11px" : "12px")};
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &:hover {
    filter: brightness(0.95);
    color: #ffffff;
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
`;

const DownloadIcon = styled.svg`
  width: 14px;
  height: 14px;
`;

// ---- Pay modal styles ------------------------------------------------------

const PayBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 90;
  padding: 16px;
`;

const PayCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px 28px;
  width: min(440px, 100%);
  box-shadow: 0 24px 56px rgba(0, 0, 0, 0.32);

  [data-theme="dark"] & {
    background: #111827;
    color: #f3f4f6;
  }
`;

const PayTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 800;
  color: ${brand_orange_deep};
`;

const PayLede = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const PaySummary = styled.div`
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 18px;

  [data-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.12);
  }
`;

const PaySummaryName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const PaySummaryRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 13px;
  color: #6b7280;

  strong {
    font-size: 18px;
    color: ${brand_orange_deep};
  }
`;

const PaySubmit = styled.button`
  width: 100%;
  padding: 12px 22px;
  border-radius: 999px;
  border: none;
  background: ${brand_gradient};
  color: #ffffff;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    filter: brightness(0.97);
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    filter: none;
  }
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const OptionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;
`;

const OptionRow = styled.button`
  text-align: left;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  background: ${({ $active }) =>
    $active ? "rgba(216, 89, 7, 0.1)" : "transparent"};
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(216, 89, 7, 0.55)" : "rgba(15, 23, 42, 0.16)"};
  color: ${({ $active }) => ($active ? brand_orange_deep : "#374151")};

  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 1px;
  }
`;

const CertField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;

  input {
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid rgba(15, 23, 42, 0.16);
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
  }
  input:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 1px;
  }
`;

const CertFieldset = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  margin-top: 12px;
  border-radius: 12px;
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.28);
`;

const CertFieldLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #b45309;
`;

const CouponRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;

  input {
    flex: 1;
    min-width: 0;
  }
`;

const ApplyBtn = styled.button`
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid rgba(216, 89, 7, 0.4);
  background: transparent;
  color: ${brand_orange_deep};
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HintOk = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: #047857;
`;

const HintErr = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: #b91c1c;
`;

const HintMuted = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: #6b7280;
`;

const PendingNote = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $expired }) => ($expired ? "#b91c1c" : "#b45309")};
`;

const PaySubscribe = styled.a`
  display: block;
  margin-top: 14px;
  font-size: 12px;
  font-weight: 600;
  color: ${brand_orange_deep};
  text-decoration: none;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`;

const PayCancel = styled.button`
  display: block;
  width: 100%;
  margin-top: 10px;
  padding: 8px;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: #374151;
  }
`;
